import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPen, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons'
import { ReactComponent as ArrowLeftIcon } from 'src/assets/svg/arrow-left-s-line.svg'
import { StatusModal } from 'src/components/StatusModal'
import { BaseModal } from 'src/containers/BaseModal'
import { CampaignSourceSelector } from 'src/components/CampaignSourceSelector'
import { CampaignsSummary } from 'src/components/CampaignSourceSelector/CampaignsSummary'
import {
  CampaignItem,
  SourceId,
} from 'src/components/CampaignSourceSelector/interfaces/campaignSource.interface'
import {
  createSelectableId,
  useCampaignSources,
} from 'src/components/CampaignSourceSelector/useCampaignSources'
import { useCreateProjectMutation } from 'src/reactQuery/useCreateProjectMutation'
import { useUpdateProjectMutation } from 'src/reactQuery/useUpdateProjectMutation'
import {
  ProjectModules,
  ProjectPayload,
} from 'src/services/projectsApi/interfaces/projects.interface'
import styles from './createProjectModal.module.scss'
import { CreateProjectModalHeader } from './CreateProjectModalHeader'
import { CreateProjectModalFooter } from './CreateProjectModalFooter'
import { WarningBanner } from './WarningBanner'

interface CreateProjectModalProps {
  activeSourceId?: ProjectCampaignSourceId
  initialIndustryIds?: string[]
  initialModules?: Partial<ProjectModules>
  initialProjectName?: string
  initialStep?: 1 | 2 | 3
  isEditMode?: boolean
  isModuleSelectionMode?: boolean
  isOpen: boolean
  lockProjectDetails?: boolean
  onClose: () => void
  onDelete?: () => void
  onSuccess?: () => void
  projectId?: string
}

export type ProjectCampaignSourceId = SourceId

const EMPTY_INDUSTRY_IDS: string[] = []
const EMPTY_MODULES: Partial<ProjectModules> = {}

const iconStrokeProps = {
  fill: 'none',
  stroke: 'currentColor',
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  strokeWidth: 1.8,
}

const HospitalityIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path d="M4 17V4.5h12V17" {...iconStrokeProps} />
    <path
      d="M3 17h14M7 7h1M10 7h1M13 7h1M7 10h1M10 10h1M13 10h1M8 17v-3h4v3"
      {...iconStrokeProps}
    />
  </svg>
)

const RealEstateIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path d="M3.5 9.5 10 4l6.5 5.5" {...iconStrokeProps} />
    <path d="M5.5 8.8V17h9V8.8" {...iconStrokeProps} />
    <path d="M8.5 17v-5h3v5" {...iconStrokeProps} />
  </svg>
)

const HealthcareIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path
      d="M10 16.5s-6-3.7-6-8.1A3.2 3.2 0 0 1 10 6a3.2 3.2 0 0 1 6 2.4c0 4.4-6 8.1-6 8.1Z"
      {...iconStrokeProps}
    />
  </svg>
)

const IctIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" {...props}>
    <path
      d="M13.62 4.3a1 1 0 1 0 0 2a4.15 4.15 0 0 1 4.06 4.07a1 1 0 0 0 2 0a6.17 6.17 0 0 0-6.06-6.07"
      fill="currentColor"
    />
    <path
      d="M13.54 0a1 1 0 0 0-1 1a1 1 0 0 0 1 1A8.63 8.63 0 0 1 22 10.45a1 1 0 0 0 1 1a1 1 0 0 0 1-1A10.64 10.64 0 0 0 13.54 0M8 8a8 8 0 1 0 8 8a8 8 0 0 0-8-8m6.16 7.12h-2.72a11.36 11.36 0 0 0-1.57-5.06a6.23 6.23 0 0 1 4.33 5.05ZM8 21.48a9.17 9.17 0 0 1-1.61-4.6h3.27A9.1 9.1 0 0 1 8 21.48m-1.57-6.37A9.24 9.24 0 0 1 8 10.5a9.15 9.15 0 0 1 1.62 4.61Zm-.21-5.06a11.36 11.36 0 0 0-1.57 5.06H1.89a6.23 6.23 0 0 1 4.33-5.06m-4.33 6.83h2.76a11.36 11.36 0 0 0 1.57 5.06a6.23 6.23 0 0 1-4.33-5.06m8 5.06a11.36 11.36 0 0 0 1.57-5.06h2.74a6.23 6.23 0 0 1-4.33 5.06Z"
      fill="currentColor"
    />
  </svg>
)

const EcommerceIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path d="M3 4h2l1.2 8.2h8.7l1.1-5.6H6" {...iconStrokeProps} />
    <path d="M8 16h.1M14 16h.1" {...iconStrokeProps} />
  </svg>
)

const ShoppingIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path d="M5 8h10l-.7 9H5.7L5 8Z" {...iconStrokeProps} />
    <path d="M7.5 8a2.5 2.5 0 0 1 5 0" {...iconStrokeProps} />
  </svg>
)

const FinanceIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path d="M4 16.5V5M4 16.5h13" {...iconStrokeProps} />
    <path d="M7.5 13.5V9.5M10.5 13.5V6.5M13.5 13.5v-3" {...iconStrokeProps} />
  </svg>
)

const TechnologyIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path d="M6.5 6.5h7v7h-7v-7Z" {...iconStrokeProps} />
    <path
      d="M8.5 1.5v3M11.5 1.5v3M8.5 15.5v3M11.5 15.5v3M1.5 8.5h3M1.5 11.5h3M15.5 8.5h3M15.5 11.5h3"
      {...iconStrokeProps}
    />
    <path d="M8.8 8.8h2.4v2.4H8.8V8.8Z" {...iconStrokeProps} />
  </svg>
)

const FoodBeverageIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path
      d="M5 7.5h9v4a4 4 0 0 1-4 4H9a4 4 0 0 1-4-4v-4Z"
      {...iconStrokeProps}
    />
    <path
      d="M14 8.5h1a2 2 0 0 1 0 4h-1M6 17h9M7 4.5v1M10 4v1M13 4.5v1"
      {...iconStrokeProps}
    />
  </svg>
)

const FmcgIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path d="m4 7 6-3 6 3-6 3-6-3Z" {...iconStrokeProps} />
    <path d="M4 7v6l6 3 6-3V7M10 10v6" {...iconStrokeProps} />
  </svg>
)

const RetailIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path
      d="M4 5.5v5.2L10.3 17 17 10.3 10.7 4H5.5A1.5 1.5 0 0 0 4 5.5Z"
      {...iconStrokeProps}
    />
    <path d="M7.5 7.5h.1" {...iconStrokeProps} />
  </svg>
)

const FitnessIcon: FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 20 20" {...props}>
    <path d="M3 10h14" {...iconStrokeProps} />
    <path d="M5 7v6M7 6v8M13 6v8M15 7v6" {...iconStrokeProps} />
  </svg>
)

export const INDUSTRIES = [
  { icon: HospitalityIcon, id: 'hospitality', label: 'Hospitality' },
  { icon: RealEstateIcon, id: 'real-estate', label: 'Real Estate' },
  { icon: HealthcareIcon, id: 'healthcare', label: 'Healthcare' },
  { icon: IctIcon, id: 'ict', label: 'ICT' },
  { icon: EcommerceIcon, id: 'e-commerce', label: 'E-Commerce' },
  { icon: ShoppingIcon, id: 'shopping', label: 'Shopping' },
  { icon: FinanceIcon, id: 'finance', label: 'Finance' },
  { icon: TechnologyIcon, id: 'technology', label: 'Technology' },
  { icon: FoodBeverageIcon, id: 'food-beverage', label: 'Food & Beverage' },
  { icon: FmcgIcon, id: 'fmcg', label: 'FMCG' },
  { icon: RetailIcon, id: 'retail', label: 'Retail' },
  { icon: FitnessIcon, id: 'fitness', label: 'Fitness' },
] as const

const buildInitialSelectedCampaignIds = (modules: Partial<ProjectModules>) => [
  ...(modules.socialListeningCampaignIds || []).map((id) =>
    createSelectableId('social-listening', id)
  ),
  ...(modules.aiListeningCampaignIds || []).map((id) =>
    createSelectableId('ai-listening', id)
  ),
  ...(modules.inboxPageIds || []).map((id) =>
    createSelectableId('inbox-analysis', id)
  ),
  ...(modules.contentAnalysisIds || []).map((id) =>
    createSelectableId('content-analysis', id)
  ),
  ...(modules.reviewAnalysisFacilities || []).flatMap(
    ({ campaignId, facilityIds }) =>
      (facilityIds || []).map((facilityId) =>
        createSelectableId('review-analysis', `${campaignId}:${facilityId}`)
      )
  ),
  ...(modules.adAnalysisPageIds || []).map((id) =>
    createSelectableId('ad-analysis', id)
  ),
]

export const CreateProjectModal: FC<CreateProjectModalProps> = ({
  activeSourceId,
  isOpen,
  onClose,
  onDelete,
  onSuccess,
  isEditMode = false,
  isModuleSelectionMode = false,
  initialProjectName = '',
  initialIndustryIds = EMPTY_INDUSTRY_IDS,
  initialModules = EMPTY_MODULES,
  initialStep = 1,
  lockProjectDetails = false,
  projectId,
}) => {
  const [currentStep, setCurrentStep] = useState<number>(1)
  const [projectName, setProjectName] = useState<string>(initialProjectName)
  const [isEditIndustrySelectionOpen, setIsEditIndustrySelectionOpen] =
    useState<boolean>(false)
  const [isEditCampaignSelectionOpen, setIsEditCampaignSelectionOpen] =
    useState<boolean>(false)
  const [selectedIndustryIds, setSelectedIndustryIds] =
    useState<string[]>(initialIndustryIds)
  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([])
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false)
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false)
  const modalCardRef = useRef<HTMLDivElement>(null)
  const projectNameInputRef = useRef<HTMLInputElement>(null)
  const isEditIndustrySelectionMode = isEditMode && isEditIndustrySelectionOpen
  const isEditCampaignSelectionMode = isEditMode && isEditCampaignSelectionOpen
  const isCampaignSelectionMode =
    isModuleSelectionMode || isEditCampaignSelectionMode
  const successModalTitle =
    isEditIndustrySelectionMode || isEditCampaignSelectionMode
      ? 'Project Updated Successfully'
      : isCampaignSelectionMode
        ? 'Campaign Added Successfully'
        : 'Project Created Successfully'
  const successModalMessage = isEditIndustrySelectionMode
    ? 'Your selected industries have been updated on this project.'
    : isEditCampaignSelectionMode
      ? 'Your selected campaigns have been added to this project. The project dashboard will now include insights from the newly linked data.'
      : isCampaignSelectionMode
        ? 'Your selected campaigns have been added to this project. The project dashboard will now include insights from the newly linked data.'
        : 'Your new project is now set up and ready to go. Start exploring insights, tracking performance, and managing your data with ease from one unified workspace.'
  const errorModalTitle = isEditIndustrySelectionMode
    ? 'Industry Couldn’t Be Added'
    : isEditCampaignSelectionMode
      ? 'Campaign Couldn’t Be Added'
      : isCampaignSelectionMode
        ? 'Campaign Couldn’t Be Added'
        : 'Project Creation Failed'
  const errorModalMessage = isEditIndustrySelectionMode
    ? 'We couldn’t update industries on this project at the moment. Please review your selection and try again.'
    : isEditCampaignSelectionMode
      ? 'We couldn’t add campaigns to this project at the moment. Please review your selection and try again.'
      : isCampaignSelectionMode
        ? 'We couldn’t add campaigns to this project at the moment. Please review your selection and try again.'
        : 'We couldn’t create your project at the moment. Please review the entered details and try again, or retry in a few moments.'
  const createProjectMutation = useCreateProjectMutation({
    onSuccess: () => {
      onClose()
      onSuccess?.()
      setIsSuccessModalOpen(true)
    },
  })
  const updateProjectMutation = useUpdateProjectMutation({
    onSuccess: () => {
      onClose()
      onSuccess?.()
      setIsSuccessModalOpen(true)
    },
  })
  const initialSelectedCampaignIds = useMemo(
    () => buildInitialSelectedCampaignIds(initialModules),
    [initialModules]
  )
  const alreadyLinkedCampaignIds = useMemo(
    () => new Set(initialSelectedCampaignIds),
    [initialSelectedCampaignIds]
  )
  const openEditCampaignSelection = () => {
    setCurrentStep(2)
    setIsEditCampaignSelectionOpen(true)
    setIsEditIndustrySelectionOpen(false)
  }
  const closeEditCampaignSelection = () => {
    setIsEditCampaignSelectionOpen(false)
    setCurrentStep(1)
  }

  const openEditIndustrySelection = () => {
    setCurrentStep(1)
    setIsEditIndustrySelectionOpen(true)
    setIsEditCampaignSelectionOpen(false)
  }
  const closeEditIndustrySelection = () => {
    setIsEditIndustrySelectionOpen(false)
    setCurrentStep(1)
  }

  const campaignSources = useCampaignSources({ enabled: isOpen })

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(initialStep)
      setProjectName(initialProjectName)
      setSelectedIndustryIds(initialIndustryIds)
      setSelectedCampaignIds(initialSelectedCampaignIds)
      setIsEditIndustrySelectionOpen(false)
      setIsEditCampaignSelectionOpen(false)
    }
  }, [
    initialIndustryIds,
    initialProjectName,
    initialSelectedCampaignIds,
    initialStep,
    isOpen,
  ])

  const handleProjectNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    setProjectName(event.target.value)
  }

  const isStepOne = currentStep === 1
  const isStepTwo = currentStep === 2
  const isEditSummaryMode =
    isEditMode && !isEditIndustrySelectionMode && !isEditCampaignSelectionMode
  const shouldLockProjectDetails = lockProjectDetails || isModuleSelectionMode
  const selectedIndustries = INDUSTRIES.filter(({ id }) =>
    selectedIndustryIds.includes(id)
  )
  const ProjectIcon = selectedIndustries[0]?.icon ?? HospitalityIcon
  // A second click while the request is in flight would POST again and fail
  // with "name already exists" even though the first one succeeded.
  const isSubmitting =
    createProjectMutation.isPending || updateProjectMutation.isPending
  const isNextDisabled =
    isSubmitting ||
    (isStepOne && (!projectName.trim() || selectedIndustryIds.length === 0)) ||
    ((isStepTwo || isCampaignSelectionMode) && selectedCampaignIds.length === 0)
  const selectedCampaignSources = campaignSources
    .map((source) => ({
      ...source,
      campaigns: source.campaigns.filter(({ id }) =>
        selectedCampaignIds.includes(id)
      ),
    }))
    .filter(({ campaigns }) => campaigns.length > 0)

  const toggleIndustry = (industryId: string) => {
    const isAlreadySelected = selectedIndustryIds.includes(industryId)

    if (isAlreadySelected) {
      setSelectedIndustryIds((prev) => prev.filter((id) => id !== industryId))
      return
    }

    if (selectedIndustryIds.length >= 3) {
      setSelectedIndustryIds((prev) => [...prev.slice(1), industryId])
      return
    }

    setSelectedIndustryIds((prev) => [...prev, industryId])
  }

  const toggleCampaign = (campaignId: string) => {
    if (alreadyLinkedCampaignIds.has(campaignId)) {
      return
    }

    const campaignSourceId = campaignId.split(':')[0] as SourceId

    if (activeSourceId && campaignSourceId !== activeSourceId) {
      return
    }

    setSelectedCampaignIds((prev) =>
      prev.includes(campaignId)
        ? prev.filter((id) => id !== campaignId)
        : [...prev, campaignId]
    )
  }

  const toggleSourceCampaigns = (campaigns: CampaignItem[]) => {
    const campaignIds = campaigns
      .filter(({ sourceId }) => !activeSourceId || sourceId === activeSourceId)
      .map(({ id }) => id)
      .filter((id) => !alreadyLinkedCampaignIds.has(id))

    if (campaignIds.length === 0) {
      return
    }

    const areAllSelected = campaignIds.every((id) =>
      selectedCampaignIds.includes(id)
    )

    setSelectedCampaignIds((prev) =>
      areAllSelected
        ? prev.filter((id) => !campaignIds.includes(id))
        : [...prev, ...campaignIds.filter((id) => !prev.includes(id))]
    )
  }

  const buildPayload = (): ProjectPayload => {
    const payload: ProjectPayload = {
      industries: selectedIndustryIds,
      name: projectName.trim(),
    }

    const getSelectedIds = (sourceId: SourceId) =>
      campaignSources
        .find((source) => source.id === sourceId)
        ?.campaigns.filter((campaign) =>
          selectedCampaignIds.includes(campaign.id)
        )
        .map((campaign) => campaign.backendId) || []

    payload.socialListeningCampaignIds = getSelectedIds('social-listening')
    payload.aiListeningCampaignIds = getSelectedIds('ai-listening')
    payload.inboxPageIds = getSelectedIds('inbox-analysis')
    payload.contentAnalysisIds = getSelectedIds('content-analysis')
    payload.adAnalysisPageIds = getSelectedIds('ad-analysis')

    // Review-analysis links whole campaigns but scopes them to the chosen
    // facilities (locations). Group selected facility rows by campaign.
    const selectedReviewRows =
      campaignSources
        .find((source) => source.id === 'review-analysis')
        ?.campaigns.filter((campaign) =>
          selectedCampaignIds.includes(campaign.id)
        ) || []
    const facilitiesByCampaign = new Map<string, string[]>()
    selectedReviewRows.forEach(({ campaignId, facilityId }) => {
      if (!campaignId || !facilityId) {
        return
      }
      const facilityIds = facilitiesByCampaign.get(campaignId) || []
      facilityIds.push(facilityId)
      facilitiesByCampaign.set(campaignId, facilityIds)
    })
    payload.reviewAnalysisCampaignIds = Array.from(facilitiesByCampaign.keys())
    payload.reviewAnalysisFacilities = Array.from(
      facilitiesByCampaign.entries()
    ).map(([campaignId, facilityIds]) => ({ campaignId, facilityIds }))

    return payload
  }

  const handleCreateProject = () => {
    createProjectMutation.mutate(buildPayload(), {
      onError: () => {
        setIsErrorModalOpen(true)
      },
    })
  }

  const renderEditIndustries = () => (
    <div className={styles.industryEditSection}>
      <div className={styles.industryEditHeader}>
        <div className={styles.industryLabel}>Industry</div>

        <button
          className={styles.industryAddButton}
          onClick={openEditIndustrySelection}
          type="button"
        >
          <FontAwesomeIcon className={styles.industryAddIcon} icon={faPlus} />
          <span>Add industry</span>
        </button>
      </div>

      <div className={styles.industryPillsGrid}>
        {selectedIndustries.map(({ icon: IndustryIcon, id, label }) => (
          <div className={styles.industryPill} key={id}>
            <div className={styles.industryPillLeft}>
              <IndustryIcon className={styles.industryPillIcon} />
              <span className={styles.industryPillLabel}>{label}</span>
            </div>
            <button
              aria-label={`Remove ${label}`}
              className={styles.industryPillRemoveButton}
              onClick={() => toggleIndustry(id)}
              type="button"
            >
              <FontAwesomeIcon
                className={styles.industryPillRemoveIcon}
                icon={faTimes}
              />
            </button>
          </div>
        ))}
      </div>
    </div>
  )

  const renderIndustrySelectionView = () => (
    <div className={styles.industrySelectionLayout}>
      <div className={styles.industrySelectionHeader}>
        <div className={styles.industryLabel}>Industry</div>
      </div>

      <div className={styles.industryCardsGrid}>
        {INDUSTRIES.map(({ icon: IndustryIcon, id, label }) => {
          const isSelected = selectedIndustryIds.includes(id)
          return (
            <button
              className={`${styles.industryCard} ${
                isSelected ? styles.industryCardSelected : ''
              }`}
              key={id}
              onClick={() => toggleIndustry(id)}
              type="button"
            >
              <IndustryIcon className={styles.industryCardIcon} />
              <span className={styles.industryCardText}>{label}</span>
            </button>
          )
        })}
      </div>
    </div>
  )

  const renderEditSummaryView = () => (
    <div className={styles.editSummaryLayout}>
      <div className={styles.editSummaryScrollFrame}>
        <div className={styles.projectNameCard}>
          <div className={styles.projectNameCardLeft}>
            <div className={styles.projectNameLabel}>
              Project name <span className={styles.required}>*</span>
            </div>
            <input
              className={styles.projectNameValue}
              onChange={handleProjectNameChange}
              ref={projectNameInputRef}
              type="text"
              value={projectName}
            />
          </div>

          <button
            aria-label="Edit project name"
            className={styles.projectNameEditButton}
            onClick={() => projectNameInputRef.current?.focus()}
            type="button"
          >
            <FontAwesomeIcon
              className={styles.projectNameEditIcon}
              icon={faPen}
            />
          </button>
        </div>

        {renderEditIndustries()}

        <div className={styles.campaignsSummaryEditSection}>
          <div className={styles.campaignsSummaryEditHeader}>
            <div className={styles.campaignsSummaryTitle}>
              Campaigns — {selectedCampaignIds.length} selected
            </div>

            <button
              className={styles.campaignsSummaryAddButton}
              onClick={openEditCampaignSelection}
              type="button"
            >
              <FontAwesomeIcon
                className={styles.campaignsSummaryAddIcon}
                icon={faPlus}
              />
              <span>Add campaign</span>
            </button>
          </div>

          {selectedCampaignSources.length > 0 ? (
            <CampaignsSummary sources={selectedCampaignSources} />
          ) : (
            <div className={styles.campaignsSummaryEmpty}>
              No campaigns linked yet.
            </div>
          )}
        </div>

        <div className={styles.atGlanceSection}>
          <div className={styles.atGlanceTitle}>At a glance</div>

          <div className={styles.atGlanceCards}>
            <div className={styles.atGlanceCard}>
              <div className={styles.atGlanceNumber}>
                {selectedCampaignIds.length}
              </div>
              <div className={styles.atGlanceLabel}>Campaigns linked</div>
            </div>

            <div className={styles.atGlanceCard}>
              <div className={styles.atGlanceNumber}>
                {selectedCampaignSources.length}
              </div>
              <div className={styles.atGlanceLabel}>Modules active</div>
            </div>

            <div className={styles.atGlanceCard}>
              <div className={styles.atGlanceNumber}>
                {selectedIndustries.length}
              </div>
              <div className={styles.atGlanceLabel}>Industries tagged</div>
            </div>
          </div>
        </div>
      </div>

      <CreateProjectModalFooter
        customLeftContent={
          onDelete ? (
            <button
              className={styles.deleteProjectButton}
              onClick={onDelete}
              type="button"
            >
              Delete Project
            </button>
          ) : null
        }
        isNextDisabled={
          isSubmitting ||
          !projectName.trim() ||
          selectedIndustryIds.length === 0
        }
        leftButtonLabel="Cancel"
        onLeftButtonClick={onClose}
        onNext={() => {
          if (
            isSubmitting ||
            !projectId ||
            !projectName.trim() ||
            selectedIndustryIds.length === 0
          ) {
            return
          }

          updateProjectMutation.mutate(
            {
              id: projectId,
              payload: buildPayload(),
            },
            {
              onError: () => {
                setIsErrorModalOpen(true)
              },
            }
          )
        }}
        rightButtonLabel="Save"
      />
    </div>
  )

  return (
    <>
      <BaseModal
        contentClassName={styles.projectModalContent}
        isOpen={isOpen}
        onRequestClose={onClose}
        shouldCloseOnOverlayClick={false}
      >
        <div
          className={`${styles.modalCard} ${isEditMode ? styles.modalCardEditMode : ''}`}
          ref={modalCardRef}
        >
          <CreateProjectModalHeader
            centerTitle={
              isEditIndustrySelectionMode || isEditCampaignSelectionMode
            }
            currentStep={currentStep}
            hideSteps={isEditMode || isModuleSelectionMode}
            leftActionAriaLabel="Back"
            leftActionIcon={
              isEditIndustrySelectionMode || isEditCampaignSelectionMode ? (
                <ArrowLeftIcon className={styles.backArrowIcon} />
              ) : undefined
            }
            onClose={onClose}
            onLeftAction={
              isEditIndustrySelectionMode
                ? closeEditIndustrySelection
                : closeEditCampaignSelection
            }
            title={
              isEditIndustrySelectionMode
                ? 'Add Industry'
                : isEditCampaignSelectionMode
                  ? 'Add Campaigns'
                  : isEditMode
                    ? 'Edit Project'
                    : isModuleSelectionMode
                      ? 'Add Campaigns to Project'
                      : 'Create New Project'
            }
          />

          {isEditIndustrySelectionMode ? (
            <div className={styles.industrySelectionLayout}>
              {renderIndustrySelectionView()}

              <CreateProjectModalFooter
                customLeftContent={
                  <span className={styles.campaignSelectionCount}>
                    <span className={styles.campaignSelectionCountValue}>
                      {selectedIndustryIds.length}
                    </span>{' '}
                    Industry selected
                  </span>
                }
                isNextDisabled={false}
                leftButtonLabel="Cancel"
                onLeftButtonClick={closeEditIndustrySelection}
                onNext={closeEditIndustrySelection}
                rightButtonLabel="Add to Project"
                widePrimaryButton
              />
            </div>
          ) : isEditCampaignSelectionMode ? (
            <div className={styles.campaignSelectionLayout}>
              <CampaignSourceSelector
                activeSourceId={activeSourceId}
                alreadyLinkedCampaignIds={alreadyLinkedCampaignIds}
                campaignSources={campaignSources}
                isCompact
                isOpen={isOpen}
                onToggleCampaign={toggleCampaign}
                onToggleSourceCampaigns={toggleSourceCampaigns}
                selectedCampaignIds={selectedCampaignIds}
              />

              <CreateProjectModalFooter
                customLeftContent={
                  <span className={styles.campaignSelectionCount}>
                    <span className={styles.campaignSelectionCountValue}>
                      {selectedCampaignIds.length}
                    </span>{' '}
                    Campaigns Selected
                  </span>
                }
                isNextDisabled={false}
                leftButtonLabel="Cancel"
                onLeftButtonClick={closeEditCampaignSelection}
                onNext={closeEditCampaignSelection}
                rightButtonLabel="Add to Project"
                widePrimaryButton
              />
            </div>
          ) : isEditSummaryMode ? (
            renderEditSummaryView()
          ) : isStepOne ? (
            <>
              <div className={styles.projectNameInput}>
                <div className={styles.projectNameLabel}>
                  Project name <span className={styles.required}>*</span>
                </div>
                <input
                  className={styles.projectNameValue}
                  onChange={handleProjectNameChange}
                  type="text"
                  value={projectName}
                />
              </div>

              {!isEditMode && (
                <WarningBanner
                  text="You can only select up to 3 industries. Please deselect one of your choices before adding a new one."
                  variant="danger"
                />
              )}

              <div className={styles.industrySection}>
                <div className={styles.industryLabel}>Industry</div>

                <div className={styles.industryCardsGrid}>
                  {INDUSTRIES.map(({ icon: IndustryIcon, id, label }) => {
                    const isSelected = selectedIndustryIds.includes(id)
                    return (
                      <button
                        className={`${styles.industryCard} ${
                          isSelected ? styles.industryCardSelected : ''
                        }`}
                        key={id}
                        onClick={() => toggleIndustry(id)}
                        type="button"
                      >
                        <IndustryIcon className={styles.industryCardIcon} />
                        <span className={styles.industryCardText}>{label}</span>
                      </button>
                    )
                  })}
                </div>
              </div>
            </>
          ) : isStepTwo ? (
            <CampaignSourceSelector
              activeSourceId={activeSourceId}
              alreadyLinkedCampaignIds={alreadyLinkedCampaignIds}
              campaignSources={campaignSources}
              isOpen={isOpen}
              measureContainerRef={modalCardRef}
              onToggleCampaign={toggleCampaign}
              onToggleSourceCampaigns={toggleSourceCampaigns}
              selectedCampaignIds={selectedCampaignIds}
              showWarningBanner
            />
          ) : (
            <div className={styles.stepThreeScrollFrame}>
              <div className={styles.stepThreeContent}>
                <div className={styles.projectSummaryLabel}>Project</div>

                <div className={styles.projectSummaryCard}>
                  <div className={styles.projectSummaryHeader}>
                    <div className={styles.projectSummaryIconBox}>
                      <ProjectIcon className={styles.projectSummaryIcon} />
                    </div>

                    <div className={styles.projectSummaryText}>
                      <div className={styles.projectSummaryName}>
                        {projectName}
                      </div>
                      <div className={styles.projectSummarySubtext}>
                        New project · Active after creation
                      </div>
                    </div>
                  </div>

                  <div className={styles.projectSummaryDivider} />

                  <div className={styles.projectSummaryIndustries}>
                    <div className={styles.projectSummaryIndustriesLabel}>
                      Industries
                    </div>

                    <div className={styles.projectSummaryPills}>
                      {selectedIndustries.map(
                        ({ icon: IndustryIcon, id, label }) => (
                          <div className={styles.projectSummaryPill} key={id}>
                            <IndustryIcon
                              className={styles.projectSummaryPillIcon}
                            />
                            <span>{label}</span>
                          </div>
                        )
                      )}
                    </div>
                  </div>
                </div>

                <div className={styles.campaignsSummarySection}>
                  <div className={styles.campaignsSummaryTitle}>
                    Campaigns — {selectedCampaignIds.length} selected
                  </div>

                  <CampaignsSummary sources={selectedCampaignSources} />
                </div>

                <div className={styles.atGlanceSection}>
                  <div className={styles.atGlanceTitle}>At a glance</div>

                  <div className={styles.atGlanceCards}>
                    <div className={styles.atGlanceCard}>
                      <div className={styles.atGlanceNumber}>
                        {selectedCampaignIds.length}
                      </div>
                      <div className={styles.atGlanceLabel}>
                        Campaigns linked
                      </div>
                    </div>

                    <div className={styles.atGlanceCard}>
                      <div className={styles.atGlanceNumber}>
                        {selectedCampaignSources.length}
                      </div>
                      <div className={styles.atGlanceLabel}>Modules active</div>
                    </div>

                    <div className={styles.atGlanceCard}>
                      <div className={styles.atGlanceNumber}>
                        {selectedIndustries.length}
                      </div>
                      <div className={styles.atGlanceLabel}>
                        Industries tagged
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {!isEditSummaryMode &&
            !isEditIndustrySelectionMode &&
            !isEditCampaignSelectionMode && (
              <div>
                <CreateProjectModalFooter
                  customLeftContent={
                    isEditCampaignSelectionMode ? (
                      <span className={styles.campaignSelectionCount}>
                        <span className={styles.campaignSelectionCountValue}>
                          {selectedCampaignIds.length}
                        </span>{' '}
                        Campaigns Selected
                      </span>
                    ) : undefined
                  }
                  footerNote={
                    currentStep === 3
                      ? 'You can edit campaigns anytime from the project dashboard'
                      : undefined
                  }
                  isNextDisabled={isNextDisabled}
                  leftButtonLabel={
                    isStepOne || (shouldLockProjectDetails && isStepTwo)
                      ? 'Cancel'
                      : 'Back'
                  }
                  onLeftButtonClick={
                    isStepOne || (shouldLockProjectDetails && isStepTwo)
                      ? onClose
                      : () => setCurrentStep((prev) => prev - 1)
                  }
                  onNext={() => {
                    if (isNextDisabled) {
                      return
                    }

                    if (isCampaignSelectionMode) {
                      if (!projectId) {
                        return
                      }

                      updateProjectMutation.mutate(
                        {
                          id: projectId,
                          payload: buildPayload(),
                        },
                        {
                          onError: () => {
                            setIsErrorModalOpen(true)
                          },
                        }
                      )
                      return
                    }

                    if (isEditMode) {
                      if (!projectId) {
                        return
                      }

                      updateProjectMutation.mutate(
                        {
                          id: projectId,
                          payload: {
                            industries: selectedIndustryIds,
                            name: projectName.trim(),
                          },
                        },
                        {
                          onError: () => {
                            setIsErrorModalOpen(true)
                          },
                        }
                      )
                      return
                    }

                    if (isModuleSelectionMode) {
                      if (!projectId) {
                        return
                      }

                      if (currentStep === 3) {
                        updateProjectMutation.mutate(
                          {
                            id: projectId,
                            payload: buildPayload(),
                          },
                          {
                            onError: () => {
                              setIsErrorModalOpen(true)
                            },
                          }
                        )
                        return
                      }

                      setCurrentStep((prev) => prev + 1)
                      return
                    }

                    if (currentStep === 3) {
                      handleCreateProject()
                      return
                    }

                    setCurrentStep((prev) => prev + 1)
                  }}
                  rightButtonLabel={
                    isCampaignSelectionMode
                      ? 'Add to Project'
                      : isEditMode
                        ? 'Save'
                        : isModuleSelectionMode
                          ? 'Add'
                          : currentStep === 3
                            ? 'Create Project'
                            : 'Next'
                  }
                  showCreateIcon={currentStep === 3 && !isCampaignSelectionMode}
                  widePrimaryButton={isCampaignSelectionMode}
                />
              </div>
            )}
        </div>
      </BaseModal>

      <StatusModal
        hideCountdown
        isOpen={isSuccessModalOpen}
        message={successModalMessage}
        onClose={() => setIsSuccessModalOpen(false)}
        title={successModalTitle}
        type="success"
      />

      <StatusModal
        doneLabel="Try Again"
        hideCountdown
        isOpen={isErrorModalOpen}
        message={errorModalMessage}
        onClose={() => setIsErrorModalOpen(false)}
        onSecondaryAction={() => setIsErrorModalOpen(false)}
        secondaryLabel="Discard"
        title={errorModalTitle}
        type="error"
      />
    </>
  )
}
